<template>
<div v-if="!readOnly || selectedFile">
	<div class="mt-8">
		<label class="block input-label">
			{{ $t('Pupil file') }}

			<!-- <span class="tooltip tooltip-right" :data-tip="$t('#info: pupil file explanation')">
				<QuestionMarkCircleIcon class="w-5 h-5 text-base-content/50 hover:cursor-help" />
			</span> -->
		</label>
	</div>

	<div class="mt-2">
		<!-- Selected file display -->
		<div v-if="selectedFile" class="input input-bordered flex items-center gap-2 px-2 py-2 rounded" :class="[readOnly? 'input-disabled text-gray-400' : '']">
			<img v-if="fileTypeIcon?.storageIconUrl" :src="fileTypeIcon.storageIconUrl" class="w-5 h-5 shrink-0" alt="" />
			<img v-else-if="fileTypeIcon?.url" :src="fileTypeIcon.url" class="w-5 h-5 shrink-0 rounded" alt="" />

			<span class="truncate text-sm flex-1" :title="selectedFile.name">{{ selectedFile.name }}</span>

			<a :href="selectedFile.url" target="_blank" class="btn btn-ghost btn-circle btn-sm shrink-0" :title="$t('Open')">
				<ArrowTopRightOnSquareIcon class="w-4 h-4" />
			</a>

			<button v-if="!readOnly" type="button" class="btn btn-ghost btn-circle btn-sm shrink-0" @click="removeFile" :title="$t('Remove')">
				<XMarkIcon class="w-4 h-4" />
			</button>
		</div>

		<!-- Loading state -->
		<div v-else-if="uploading" class="flex items-center gap-2 px-2 py-2 text-sm text-base-content-light">
			<span class="loading loading-spinner loading-sm"></span>
			{{ $t('Loading') }}...
		</div>

		<!-- Picker buttons — only shown to admins (non-readOnly) -->
		<div v-else-if="!readOnly">
			<GoogleDrivePicker
				v-if="usersStore.me.hasCapability('google-drive')"
				@filesSelected="selectExternalFiles"
				class="input input-bordered text-xs flex items-center gap-2 w-full"
				:label="$t('Pick from Google Drive')"
				:accepts="accepts"
			/>
			<OneDrivePicker
				v-if="usersStore.me.hasCapability('onedrive')"
				@filesSelected="selectExternalFiles"
				class="input input-bordered text-xs flex items-center gap-2 w-full"
				:label="$t('Pick from OneDrive')"
				:accepts="accepts"
			/>
		</div>
	</div>
</div>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import { useUploadStore } from "@/stores/Upload.store";
import { useUsersStore } from "@/stores/Users.store";
import GoogleDrivePicker from "@/components/GoogleDrivePicker.vue";
import OneDrivePicker from "@/components/OneDrivePicker.vue";
import { FileExternal } from "@/models/FileExternal.model";
import { File as FileModel } from "@/models/File.model";
import { QuestionMarkCircleIcon, ArrowTopRightOnSquareIcon, XMarkIcon } from "@heroicons/vue/24/outline";

export default {
	emits: ['update:modelValue', 'update:uploading'],

	components: {
		GoogleDrivePicker,
		OneDrivePicker,
		QuestionMarkCircleIcon,
		ArrowTopRightOnSquareIcon,
		XMarkIcon,
	},

	props: {
		modelValue: {
			type: [FileModel, null] as any,
			default: null,
		},
		uploading: {
			type: Boolean,
			default: false,
		},
		/**
		 * Controls which entity types can be selected.
		 * Accepted values: 'files', 'folders' or both.
		 * Defaults to ['files', 'folders'].
		 */
		accepts: {
			type: Array as () => Array<'files' | 'folders'>,
			default: () => ['files', 'folders'],
			validator: (value: string[]) => value.every(v => ['files', 'folders'].includes(v)),
		},
		/**
		 * When true, the file cannot be changed or removed.
		 * Only the current file (if any) is displayed.
		 */
		readOnly: {
			type: Boolean,
			default: false,
		},
	},

	computed: {
		...mapStores(useUploadStore, useUsersStore),

		selectedFile(): FileModel | null {
			return this.modelValue ?? null;
		},

		fileTypeIcon() {
			return this.selectedFile?.getFileTypeIcon() ?? null;
		},
	},

	methods: {
		async selectExternalFiles(files: FileExternal[]) {
			if (!files.length) return;

			// Only take the first file
			const file = files[0];

			this.$emit('update:uploading', true);

			try {
				const uploadedFile = await this.uploadStore.addFromExternalStorage(file);
				this.$emit('update:uploading', false);
				this.$emit('update:modelValue', uploadedFile);
			} catch (e) {
				this.$emit('update:uploading', false);

				console.error(e);
				this.$notify({ text: this.$t('Could not add the file {name}', { name: file.name }), type: 'error' });
			}
		},

		removeFile() {
			this.$emit('update:modelValue', null);
		},
	},
};
</script>
